import {
  CHANNELS,
  NOTIFICATION_EVENTS,
  RUN_EVENTS,
  echoEventName,
  isRunEventPayload,
  type NotificationEventPayload,
  type RunEventPayload,
} from './contract';
import { createRunStore, type RunStore } from './run-store';

/**
 * The Reverb client (P0-WEB-05).
 *
 * Thin on purpose. pusher-js already owns the hard part of "reconnect and
 * resubscribe": it backs off, reconnects, and re-subscribes every channel it
 * held, re-running the authorizer for each. Reimplementing that would be worse
 * and is not what the task is short of.
 *
 * What the portal owns is the part no socket library can do for it — from the
 * contract's client rules:
 *
 *   "Realtime is an accelerator, never the source of truth. Reconnect must
 *    re-fetch run state over HTTP, because events emitted while the socket was
 *    down are not replayed."
 *
 * So `onReconnect` fires on every connection *after* the first, and the app
 * wires it to `router.revalidate()`, which re-runs every active loader. Without
 * it a user who closed their laptop for ten minutes comes back to a progress
 * bar frozen at whatever it read when the socket dropped, with no indication
 * that it is stale.
 */

/** The slice of Laravel Echo this client uses, so tests need no socket. */
export interface EchoChannel {
  listen(event: string, callback: (payload: unknown) => void): unknown;
  stopListening(event: string, callback?: (payload: unknown) => void): unknown;
}

export interface EchoConnector {
  onConnectionChange(callback: (status: string) => void): () => void;
  connectionStatus(): string;
}

export interface EchoLike {
  connector: EchoConnector;
  private(channel: string): EchoChannel;
  leaveChannel(channel: string): void;
  disconnect(): void;
}

/** Reduced from Echo's five states to the three a UI can act on. */
export type ConnectionState = 'connecting' | 'connected' | 'disconnected';

export interface ConnectionStore {
  readonly subscribe: (listener: (state: ConnectionState) => void) => () => void;
  readonly getSnapshot: () => ConnectionState;
}

export interface RunEventHandlers {
  /** Every run event, already validated and applied to the store. */
  readonly onRun?: (payload: RunEventPayload) => void;
}

export interface RealtimeClientOptions {
  readonly echo: EchoLike;
  /**
   * Called when the socket reconnects after having been connected before —
   * never on the first connection, where the loaders have just run.
   */
  readonly onReconnect?: () => void;
  readonly runs?: RunStore;
}

export interface RealtimeClient {
  readonly runs: RunStore;
  readonly connection: ConnectionStore;
  /**
   * Late-bound, in the style of `observability/error-reporter.ts`. The client
   * is created once and lives for the page; the thing it must call on
   * reconnection is `revalidate()` from a router that did not exist yet, and
   * which is replaced on every navigation.
   */
  readonly setOnReconnect: (handler: (() => void) | undefined) => void;
  readonly subscribeToOrganisationRuns: (
    organisationId: string,
    handlers?: RunEventHandlers,
  ) => () => void;
  readonly subscribeToUserNotifications: (
    userId: string,
    onNotification: (payload: NotificationEventPayload) => void,
  ) => () => void;
  readonly disconnect: () => void;
}

export function createRealtimeClient({
  echo,
  onReconnect,
  runs = createRunStore(),
}: RealtimeClientOptions): RealtimeClient {
  let reconnectHandler = onReconnect;
  const connection = createConnectionStore(echo, () => reconnectHandler?.());

  // Ref-counted, because a workspace can mount two things watching the same
  // organisation. Leaving the channel when the first of them unmounts would
  // stop delivery to the second, and nothing would report it.
  const subscriptions = new Map<string, { count: number; teardown: () => void }>();

  function hold(channelName: string, attach: (channel: EchoChannel) => () => void): () => void {
    const existing = subscriptions.get(channelName);

    if (existing !== undefined) {
      existing.count++;
    } else {
      const channel = echo.private(channelName);
      subscriptions.set(channelName, { count: 1, teardown: attach(channel) });
    }

    let released = false;
    return () => {
      // Guarded: a double-invoked cleanup — React 19 StrictMode mounts twice in
      // development — would otherwise decrement past zero and leave the channel
      // held by a count that can never reach it.
      if (released) return;
      released = true;

      const entry = subscriptions.get(channelName);
      if (entry === undefined) return;

      entry.count--;
      if (entry.count > 0) return;

      entry.teardown();
      echo.leaveChannel(channelName);
      subscriptions.delete(channelName);
    };
  }

  return {
    runs,
    connection,

    setOnReconnect(handler) {
      reconnectHandler = handler;
    },

    subscribeToOrganisationRuns(organisationId, handlers = {}) {
      return hold(CHANNELS.organisationRuns(organisationId), (channel) => {
        const bound = Object.values(RUN_EVENTS).map((event) => {
          const listener = (payload: unknown) => {
            // Validated rather than trusted: an event with no run_id would
            // create a phantom run in the store that nothing can complete.
            if (!isRunEventPayload(payload)) return;
            runs.apply(payload);
            handlers.onRun?.(payload);
          };

          channel.listen(echoEventName(event), listener);
          return { event, listener };
        });

        return () => {
          for (const { event, listener } of bound) {
            channel.stopListening(echoEventName(event), listener);
          }
        };
      });
    },

    subscribeToUserNotifications(userId, onNotification) {
      return hold(CHANNELS.userNotifications(userId), (channel) => {
        const event = echoEventName(NOTIFICATION_EVENTS.raised);
        const listener = (payload: unknown) => {
          if (typeof payload !== 'object' || payload === null) return;
          onNotification(payload as NotificationEventPayload);
        };

        channel.listen(event, listener);
        return () => {
          channel.stopListening(event, listener);
        };
      });
    },

    disconnect() {
      for (const [name, entry] of subscriptions) {
        entry.teardown();
        echo.leaveChannel(name);
      }
      subscriptions.clear();
      echo.disconnect();
    },
  };
}

function createConnectionStore(echo: EchoLike, onReconnect: () => void): ConnectionStore {
  let state = normalise(echo.connector.connectionStatus());
  const listeners = new Set<(state: ConnectionState) => void>();

  // Distinguishes a first connection from a reconnection. Re-fetching on the
  // first one would duplicate the loaders that have only just run.
  let hasConnected = state === 'connected';

  echo.connector.onConnectionChange((status) => {
    const next = normalise(status);

    if (next === 'connected') {
      const isReconnect = hasConnected;
      hasConnected = true;
      // Fired before the state is published, so anything reacting to
      // "connected" sees a revalidation already in flight rather than a moment
      // of stale data presented as live.
      if (isReconnect) onReconnect();
    }

    if (next === state) return;
    state = next;
    for (const listener of listeners) listener(state);
  });

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      return state;
    },
  };
}

/**
 * Echo reports five states; a user can only act on three.
 *
 * `failed` is folded into `disconnected` deliberately: to somebody watching a
 * run, "the browser cannot use WebSockets here" and "the connection dropped"
 * call for the same thing — trust the page, not the socket — and the page keeps
 * working either way because every number on it came over HTTP first.
 */
function normalise(status: string): ConnectionState {
  switch (status) {
    case 'connected':
      return 'connected';
    case 'connecting':
    case 'reconnecting':
      return 'connecting';
    default:
      return 'disconnected';
  }
}
